import React, { useEffect, useState } from "react";
import { Search, Check } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import api from "@/lib/api";

// UI labels — single "Interested" toggle (DB value: connect)
const LABELS = {
  connect: "Interested",
};

const AttendeeCard = ({ attendee, onSelect, onRemove, saving }) => {
  const sel = attendee.my_interest === "connect";

  return (
    <div className="mm-card-static" data-testid={`profile-card-${attendee.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-2xl" style={{ color: "#0F172A" }}>
            {attendee.first_name}
          </div>
          <div className="text-sm text-slate-600 mt-1">{attendee.occupation}</div>
          <div className="overline mt-2">Age {attendee.age}</div>
        </div>
      </div>

      <div className="mt-5">
        {sel ? (
          <button
            disabled={saving}
            data-testid={`card-${attendee.id}-remove`}
            onClick={() => onRemove(attendee.id)}
            className="mm-btn-primary"
            style={{ padding: "12px 16px", fontSize: 13 }}
          >
            <Check size={14} strokeWidth={2.5} className="inline mr-2" />
            Interested · Tap to remove
          </button>
        ) : (
          <button
            disabled={saving}
            data-testid={`card-${attendee.id}-connect`}
            onClick={() => onSelect(attendee.id, "connect")}
            className="mm-btn-outline"
            style={{ padding: "12px 16px", fontSize: 13 }}
          >
            Interested
          </button>
        )}
      </div>
    </div>
  );
};

const AttendeesPage = () => {
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const loadUsage = async () => {
    try {
      const res = await api.get("/interest/usage");
      setUsage(res.data);
    } catch (err) {
      console.warn("Failed to refresh usage", err);
    }
  };

  const load = async (q = "") => {
    try {
      const res = await api.get("/attendees", { params: q ? { search: q } : {} });
      setItems(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadUsage(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const onSelect = async (attendeeId, level) => {
    setSavingId(attendeeId);
    try {
      await api.post("/interest", { target_user_id: attendeeId, level });
      setItems((arr) => arr.map((a) => (a.id === attendeeId ? { ...a, my_interest: level } : a)));
      loadUsage();
      toast.success("Marked as Interested");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSavingId(null);
    }
  };

  const onRemove = async (attendeeId) => {
    setSavingId(attendeeId);
    try {
      await api.delete(`/interest/${attendeeId}`);
      setItems((arr) => arr.map((a) => (a.id === attendeeId ? { ...a, my_interest: null } : a)));
      loadUsage();
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Shell title="Attendees">
      {usage && (
        <div
          className="mb-5 px-4 py-3 border border-slate-200 rounded-sm bg-white flex items-center justify-between"
          data-testid="usage-indicator"
        >
          <div>
            <div className="overline text-[10px]">Your selections</div>
            <div className="text-sm mt-0.5">
              <span className="font-semibold" style={{ color: "#0F172A" }}>{usage.used}</span>
              <span className="text-slate-500"> of {usage.max} used</span>
            </div>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: usage.max }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-6 rounded-sm"
                style={{ background: i < usage.used ? "#0F172A" : "#E2E8F0" }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-6">
        <Search size={16} strokeWidth={1.5} color="#64748B" className="absolute left-0 top-1/2 -translate-y-1/2" />
        <input
          data-testid="attendees-search"
          className="mm-input pl-7"
          placeholder="Search by first name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <p className="text-xs text-slate-500 mb-5 leading-relaxed" data-testid="legend">
        Tap <strong className="text-slate-700">Interested</strong> on anyone you'd like to be matched with (up to 5). Leave the rest untouched — there's no need to mark "not interested". A match happens only when both people pick Interested.
      </p>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <div className="overline mb-2">No results</div>
          <p className="text-sm text-slate-600">
            {search ? "Try a different name." : "Attendees will appear here as they join."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="attendees-list">
          {items.map((a) => (
            <AttendeeCard key={a.id} attendee={a} onSelect={onSelect} onRemove={onRemove} saving={savingId === a.id} />
          ))}
        </div>
      )}
    </Shell>
  );
};

export default AttendeesPage;
